import { createSlice } from '@reduxjs/toolkit';
import { Setting, SettingKey } from '../../models/setting.model';
import { ContentModel } from '../../models/content.model';
import { Booking, CardlinkPaymentFormData } from '../../models/booking.model';
import { AvailabilityDay } from '../../models/availability.model';
import { PromoCodeValidationResult } from '../../models/promocode.model';
import defaultContent from 'utils/data';

interface PublicState {
    availability: AvailabilityDay[];
    settings: Setting[];
    booking: Booking | null;
    redirectUrl: string | null;
    form_data: CardlinkPaymentFormData | null;
    bookingCreateLoading: boolean;
    loading: boolean;
    bookingError: string | null;
    content: ContentModel | null;
    emailLoading: boolean;
    emailError: string | null;
    promoCodeValidation: PromoCodeValidationResult | null;
    promoCodeLoading: boolean;
    logLoading: boolean;
}

const initialState: PublicState = {
    availability: [],
    settings: [],
    booking: null,
    redirectUrl: null,
    form_data: null,
    bookingCreateLoading: false,
    loading: false,
    bookingError: null,
    content: defaultContent as unknown as ContentModel,
    emailLoading: false,
    emailError: null,
    promoCodeValidation: null,
    promoCodeLoading: false,
    logLoading: false,
};

const publicSlice = createSlice({
    name: 'public',
    initialState,
    reducers: {
        createBooking: (state) => {
            state.bookingCreateLoading = true;
        },
        createBookingSuccess: (state, action) => {
            state.booking = action.payload;
            state.redirectUrl = action.payload.redirectUrl;
            state.form_data = action.payload.form_data;
            state.bookingCreateLoading = false;
        },
        createBookingFailed: (state, action) => {
            state.bookingCreateLoading = false;
            state.bookingError = action.payload;
        },
        getAvailability: (state) => {
            state.loading = true;
        },
        getAvailabilitySuccess: (state, action) => {
            state.availability = action.payload.monthData || [];
            state.loading = false;
        },
        getAvailabilityFailed: (state) => {
            state.loading = false;
        },
        getPublicSettings: (state) => {
            state.loading = true;
        },
        getPublicSettingsSuccess: (state, action) => {
            state.settings = action.payload.filter((setting: Setting) => setting.setting_key !== SettingKey.PAGE_CONTENT);
            state.content =  action.payload.find((setting: Setting) => setting.setting_key === SettingKey.PAGE_CONTENT)?.setting_value as unknown as ContentModel || defaultContent;
            state.loading = false;
        },
        getPublicSettingsFailed: (state) => {
            state.loading = false;
            state.content = defaultContent as unknown as ContentModel;
            state.settings = [];
        },
        sendEmail: (state) => {
            state.emailLoading = true;
            state.emailError = null;
        },
        sendEmailSuccess: (state) => {
            state.emailLoading = false;
        },
        sendEmailFailed: (state, action) => {
            state.emailLoading = false;
            state.emailError = action.payload;
        },
        validatePromoCode: (state) => {
            state.promoCodeLoading = true;
        },
        validatePromoCodeSuccess: (state, action) => {
            state.promoCodeLoading = false;
            state.promoCodeValidation = action.payload;
        },
        validatePromoCodeFailed: (state) => {
            state.promoCodeLoading = false;
            state.promoCodeValidation = { valid: false, code: '', discount_value: 0, discount_type: null, type: null, message: 'Invalid promo code' };
        },
        clearPromoCode: (state) => {
            state.promoCodeValidation = null;
            state.promoCodeLoading = false;
        },
        createPublicLog: (state) => {
            state.logLoading = true;
        },
        createPublicLogSuccess: (state) => {
            state.logLoading = false;
        },
        createPublicLogFailed: (state) => {
            state.logLoading = false;
        }
    },
});

export const {
    createBooking,
    createBookingSuccess,
    createBookingFailed,
    getAvailability,
    getAvailabilitySuccess,
    getAvailabilityFailed,
    getPublicSettings,
    getPublicSettingsSuccess,
    getPublicSettingsFailed,
    sendEmail,
    sendEmailSuccess,
    sendEmailFailed,
    validatePromoCode,
    validatePromoCodeSuccess,
    validatePromoCodeFailed,
    clearPromoCode,
    createPublicLog,
    createPublicLogSuccess,
    createPublicLogFailed,
} = publicSlice.actions;

export default publicSlice.reducer; 